import React from "react";
import { useNavigate } from "react-router-dom";

import { RealEstateSourceBadge } from "@features/real-estate/components/source-badge";
import { formatCurrency, formatNumber, formatPercentPoints } from "@features/real-estate/components/format";
import { useRealEstateEnrichmentMutation } from "@features/real-estate/queries/enrichment";
import { useRealEstateWorkspace } from "@features/real-estate/state/workspace";
import type { RealEstateEnrichmentModule, RealEstateModuleKey } from "@features/real-estate/types";

const MODULE_ORDER: RealEstateModuleKey[] = ["location", "neighborhood", "rent", "hazard", "rates", "taxes"];

export default function RealEstateContextScreen(): React.ReactElement {
  const { state } = useRealEstateWorkspace();
  const enrichmentMutation = useRealEstateEnrichmentMutation();
  const navigate = useNavigate();

  const handleRefresh = async () => {
    try {
      await enrichmentMutation.mutateAsync();
    } catch {
      // state already captures the error
    }
  };

  return (
    <section className="re-screen">
      <div className="re-screen-header">
        <div>
          <h2>Context and diligence</h2>
          <p>Each module is independent. One public source failing should not block the rest of the underwriting workspace.</p>
        </div>
        <div className="re-action-row">
          <button type="button" className="re-button" onClick={handleRefresh} disabled={enrichmentMutation.isPending}>
            {enrichmentMutation.isPending ? "Refreshing..." : "Refresh context"}
          </button>
          <button type="button" className="re-button re-button-primary" onClick={() => navigate("/real-estate/underwrite")}>
            Open underwrite
          </button>
        </div>
      </div>

      {state.enrichmentError ? (
        <div className="re-callout re-callout-danger">
          Context request failed: {state.enrichmentError}
        </div>
      ) : null}

      {state.enrichment?.resolvedProperty ? (
        <section className="re-panel re-summary-panel">
          <div>
            <div className="re-panel-title">Resolved property</div>
            <h3>{state.enrichment.resolvedProperty.matchedAddress}</h3>
            <p>
              {state.enrichment.resolvedProperty.countyName || "Unknown county"}
              {" · "}
              tract {state.enrichment.resolvedProperty.tractFips || "N/A"}
            </p>
          </div>
          <div className="re-summary-meta">
            <span>Lat {formatCoordinate(state.enrichment.resolvedProperty.latitude)}</span>
            <span>Lng {formatCoordinate(state.enrichment.resolvedProperty.longitude)}</span>
          </div>
        </section>
      ) : null}

      <div className="re-module-grid">
        {MODULE_ORDER.map((key) => {
          const module = state.enrichment?.modules[key];
          return (
            <article key={key} className="re-panel re-module-card">
              <div className="re-module-header">
                <div>
                  <div className="re-panel-title">{module?.title || fallbackTitle(key)}</div>
                  <div className={`re-module-status re-module-status-${module?.status || "unavailable"}`}>
                    {module?.status || "unavailable"}
                  </div>
                </div>
              </div>

              {module?.warnings?.length ? (
                <ul className="re-warning-list">
                  {module.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : null}

              <dl className="re-module-data">
                {module ? renderModuleData(module) : <div className="re-empty-state">Run enrichment to populate this module.</div>}
              </dl>

              {module?.sources?.length ? (
                <div className="re-source-list">
                  {module.sources.map((source) => (
                    <RealEstateSourceBadge key={`${module.key}-${source.name}-${source.url || "local"}`} source={source} />
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function renderModuleData(module: RealEstateEnrichmentModule): React.ReactNode {
  const entries = Object.entries(module.data || {}).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (entries.length === 0) {
    return <div className="re-empty-state">No data returned for this module yet.</div>;
  }
  return entries.map(([key, value]) => (
    <React.Fragment key={`${module.key}-${key}`}>
      <dt>{humanizeKey(key)}</dt>
      <dd>{formatModuleValue(key, value)}</dd>
    </React.Fragment>
  ));
}

function formatModuleValue(key: string, value: unknown): string {
  if (typeof value === "number") {
    if (key.toLowerCase().includes("income") || key.toLowerCase().includes("rent") || key.toLowerCase().includes("value") || key.toLowerCase().includes("loss")) {
      return formatCurrency(value);
    }
    if (key.toLowerCase().includes("rate") && !key.toLowerCase().includes("rating")) {
      return formatPercentPoints(value);
    }
    if (key.toLowerCase().includes("latitude") || key.toLowerCase().includes("longitude")) {
      return value.toFixed(4);
    }
    return formatNumber(value);
  }
  return String(value);
}

function humanizeKey(value: string): string {
  return value.replace(/([A-Z])/g, " $1").replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase());
}

function fallbackTitle(key: RealEstateModuleKey): string {
  return humanizeKey(key);
}

function formatCoordinate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "N/A";
  }
  return value.toFixed(4);
}
